import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StateChip } from '../../components/atoms.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { scopeHintFrom } from '../../lib/scope-hint.js';
import {
  isConfirmed,
  isEvidenceDrifted,
  isOrphaned,
  isVisibleSignoffCard,
  signoffApi,
} from '../../lib/signoff-api.js';

// #55/Task 4: Wer einen Testplan ansieht, sah bisher nirgends, welche Struktur-Knoten darin schon
// abgenommen sind — nur die Sign-off-Seite selbst zeigt das. Derselbe Query-Key wie dort
// (['signoffs', projectId, planId]) sorgt dafür, dass eine dort erteilte Abnahme HIER ohne
// eigenen Refetch aktuell ist (react-query cached den Eintrag projektweit unter diesem Key).
// Ohne sign-off-fähige Knoten (oder solange die Abfrage noch lädt) gibt es keinen leeren
// Abschnitt mit Überschrift — die Komponente rendert dann `null`.
//
// Whole-Branch-Review (Wichtig): `data` ist seit dem Umbau von #83 wieder die VOLLE, ungefilterte
// Liste. Vorher filterte der Server leere Knoten (kein Testfall im Plan-Scope) bereits heraus —
// deckte KEIN Knoten Scope in diesem Plan ab, war `data` leer und dieser Block verschwand
// wortlos, exakt wie im Fall "keine sign-off-fähigen Knoten" — zwei fachlich verschiedene
// Zustände, die identisch (und stumm) aussahen. Jetzt unterscheidet die Komponente sie: gar keine
// Knoten -> weiterhin `null` (unverändert); Knoten vorhanden, aber keiner mit Testfall in DIESEM
// Plan -> derselbe Hinweistext wie auf der Sign-off-Seite (`signoff.allEmptyInPlan`), statt zu
// verschwinden. Ein Knoten MIT aktivem Sign-off bleibt immer sichtbar (isVisibleSignoffCard),
// auch wenn seine Grundlage inzwischen weg ist — dieselbe Regel wie auf der Sign-off-Seite.
export function PlanSignoffStatus({
  projectId,
  planId,
}: {
  projectId: string;
  planId: string;
}) {
  const { t } = useTranslation();
  const { user } = useScopedUser(projectId);
  const { data } = useQuery({
    queryKey: ['signoffs', projectId, planId],
    queryFn: () => signoffApi.list(projectId, planId),
    enabled: Boolean(projectId) && Boolean(planId),
  });

  // Kein sign-off-fähiger Knoten im Projekt überhaupt -> nichts zu erklären, `null` bleibt richtig.
  if (!data || data.length === 0) return null;

  const visible = data.filter(isVisibleSignoffCard);

  // Fund 3 (Whole-Branch-Review): `data` ist seit dem Knoten-Filter (docs/requirements.md §5) für
  // einen zugeschnittenen Verantwortlichen nur noch SEIN Knoten — die Kachel sieht dabei aber
  // vollständig aus (der Kommentar oben warnt seit Task 4 genau vor dieser Fehlerklasse: "wortlos
  // verschwinden, zwei fachlich verschiedene Zustände sehen identisch aus"). Knotenliste = `data`
  // selbst (trägt node_id/node_name schon) — keine neue Abfrage.
  const nodesForHint = data.map((it) => ({ node_id: it.node_id, name: it.node_name }));
  const scopeHint = scopeHintFrom(user, nodesForHint);

  return (
    <div className="rounded-card border border-[var(--border)] bg-bg-surface">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] px-4 py-3">
        <span className="text-sm font-semibold text-text-primary">{t('plans.signoffStatus')}</span>
        {/* Klein gehalten (die Kachel selbst ist klein, 1280×800-Zielschirm) — kein eigener Absatz
            wie auf Reporting/Sign-off, aber dieselbe aria-live-Begründung: einzige Warnung, dass
            hier nur ein Ausschnitt steht. */}
        {scopeHint ? (
          <span className="text-xs text-text-muted" data-testid="scope-hint" aria-live="polite">
            {scopeHint.label !== null
              ? t('scope.hintPlanSignoff', { nodes: scopeHint.label })
              : t('scope.hintPlanSignoffFallback')}
          </span>
        ) : null}
      </div>
      {visible.length === 0 ? (
        // Knoten existieren, aber keiner deckt einen Testfall in DIESEM Plan ab — derselbe
        // Wortlaut wie auf der Sign-off-Seite (signoff.allEmptyInPlan), statt wortlos zu verschwinden.
        <div className="px-4 py-3 text-sm text-text-muted">{t('signoff.allEmptyInPlan')}</div>
      ) : (
        <ul>
          {visible.map((item) => {
            const confirmed = isConfirmed(item);
            // Ein verwaister Sign-off (bestätigt, aber sein letzter Testfall ist inzwischen aus dem
            // Plan-Scope) darf auch HIER nicht als schlichtes „Bestätigt" durchgehen — sonst endet
            // die Hervorhebung an der Sign-off-Seite, und ausgerechnet im Plan, wo der Scope
            // geändert wurde, sieht alles in Ordnung aus.
            const orphaned = isOrphaned(item);
            // #88: dieselbe Hervorhebung bei geänderter Grundlage (Drift) — nicht nur dann, wenn
            // sie ganz weggefallen ist.
            const drifted = isEvidenceDrifted(item);
            return (
              <li
                key={item.node_id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm"
              >
                <span className="text-text-body">{item.node_name}</span>
                <span className="flex items-center gap-2">
                  <StateChip
                    label={confirmed ? t('signoff.confirmed') : t('signoff.open')}
                    tone={confirmed && !orphaned && !drifted ? 'ok' : 'warn'}
                  />
                  {orphaned ? (
                    <span className="text-xs text-status-warn">{t('signoff.orphanedShort')}</span>
                  ) : null}
                  {drifted ? (
                    <span className="text-xs text-status-warn">
                      {t('signoff.evidenceDriftShort')}
                    </span>
                  ) : null}
                  {confirmed && item.confirmer_name && item.confirmed_at ? (
                    <span className="text-xs text-text-muted">
                      {t('signoff.confirmedBy', {
                        name: item.confirmer_name,
                        date: item.confirmed_at.slice(0, 10),
                      })}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="border-t border-[var(--border)] px-4 py-2">
        <Link
          to={`/signoff?test_plan_id=${planId}`}
          className="text-xs font-semibold text-brand-accent hover:underline"
        >
          {t('plans.signoffOpenPage')}
        </Link>
      </div>
    </div>
  );
}
